<template>
  <div class="scheduler-container scheduler-timeline">
    <div class="scheduler-top-margin"></div>
    <div class="scheduler-zoom-buttons">
      <button type="button" class="btn btn-default btn-xs" style="height: 44px; padding: 0 12px" v-on:click="zoomIn"><i class="fa fa-search-plus"></i></button>
      <button type="button" class="btn btn-default btn-xs" style="height: 44px; padding: 0 12px" v-on:click="zoomOut"><i class="fa fa-search-minus"></i></button>
    </div>
    <div class="scheduler-controls">
      <button type="button" class="btn btn-default btn-xs" v-on:click="moveBack"><i class="fa fa-arrow-left"></i></button>
      <DatePicker v-model="dateStart" :shortLocale="shortLocale" :staticInput="true" :format="dateFormat.replace(/D/g, 'd').replace(/Y/g, 'y')"></DatePicker>
      <TimePicker v-model="timeStart" :shortLocale="shortLocale" :format="zeroTimeFormat" :disableMinutes="true" v-if="mode === 'hourly'"></TimePicker>
      <button type="button" class="btn btn-default btn-xs" v-on:click="moveForward"><i class="fa fa-arrow-right"></i></button>
      <button type="button" class="btn btn-default btn-xs" style="margin-left: 5px; padding: 0 12px" v-on:click="moveToToday" :title="_('Today')"><i class="fa fa-calendar-day"></i></button>
    </div>
    <div class="scheduler-wrapper" ref="wrapper">
      <div ref="scheduler" :class="'scheduler ' + (this.skew ? '' : 'disable-skew ') + 'scheduler-' + mode">
        <div ref="schedulerelement" class="dhx_cal_container" :style="containerStyle">
          <div class="dhx_cal_navline" v-show="isNavVisible">
            <div class="dhx_cal_date"></div>
          </div>
          <div class="dhx_cal_header"></div>
          <div class="dhx_cal_data"></div>
        </div>
        <component v-if="product" :column="column" :activeComponent="component" :mode="mode" :baseDomain="baseDomain" :key="product.id" :is="'ReservableProductListItem'" :product="product" :productShopId="productShopId" :auto="true" :summary="true" :summaryStart="summaryStart" :summaryEnd="summaryEnd" :allowResourceSelection="true" :preselectedResource="preselectedResource" :defaultStartDate="productStartDate" :defaultEndDate="productEndDate" v-on:productModalClosed="productModalClosed" v-on:success="reservationAddedToBasket">
          {{ product.name }}
        </component>
      </div>
    </div>
  </div>
</template>

<script>

import 'dhtmlx-scheduler/codebase/dhtmlxscheduler_material.css'
import 'dhtmlx-scheduler'
import escapeHtml from '~/lib/escapehtml'
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_timeline'
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_multisection'
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_treetimeline'
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_container_autoresize'
import axios from 'axios'
import { DateTime } from 'luxon'
import errors from '~/lib/errors'
import events from '~/lib/events'
import modals from '~/lib/modals'
import { mapGetters } from 'vuex'
import { DirectReservation, ReservableProductListItem, ReservationPreview, ProductPopup, DatetimePicker } from '#components'
import debounce from 'lodash/debounce'
import memoize from 'lodash/memoize'
import { guardSchedulerRender, schedulerEventHasDates } from '~/lib/schedulerGuards'

export default {
  data () {
    return {
      product: null,
      productShopId: null,
      productStartDate: null,
      productEndDate: null,
      originalEvent: null,
      scheduler: null,
      currentZoom: 3,
      currentDate: false,
      openSections: [],
      loadedEvents: {},
      preselectedResource: null,
      preventFolderToggle: false,
      summaryStart: null,
      summaryEnd: null,
      range: {},
      isNavVisible: true,
      reloader: null,
      fromTimeline: false,
      fromPicker: false,
      pickerHours: 8,
      headerTopValue: 0,
      headerLeftValue: 0,
      scrollTop: 0,
      isClickEvent: false, 
      isClickEventTimeout: null,
      timeStart: { hours: 8, minutes: 0, seconds: 0 },
      currentStorageKey: null,
      containerHeight: null,
      observer: null,
      resizeTimeouts: [],
      debouncedResizeHandler: null,
      isRestoringViewState: false,
      schedulerLoadCount: 0
    }
  },
  name: 'timeline',
  mixins: [ errors, events, modals ],
  props: ['url', 'products', 'resourcePools', 'resources', 'shopId', 'locale', 'component', 'column', 'baseDomain', 'apiKey', 'reloadEvents', 'weekStart', 'productFilter', 'resourceFilter', 'mode', 'skew', 'keepTimes', 'maxHeight'],
  computed: {
    ...mapGetters(['dateFormat', 'timeFormat', 'pickerDateFormat', 'pickerTimeFormat', 'timezone']),
    containerStyle () {
      if (!this.containerHeight) {
        return { width: '100%' }
      }
      const height = typeof this.containerHeight === 'number'
        ? this.containerHeight + 'px'
        : this.containerHeight
      return {
        width: '100%',
        height
      }
    },
    zeroTimeFormat () {
      return this.timeFormat.replace(/mm/, '00')
    },
    defaultDateFormat () {
      switch(this.pickerDateFormat) {
        case 'D.M.YYYY':
          return '%j.%n.%Y'
        case 'M.D.YYYY':
          return '%n.%j.%Y'
        case 'YYYY.MM.DD':
          return '%Y.%n.%j'
        case 'DD-MM-YYYY':
          return '%d-%m-%Y'
        case 'MM-DD-YYYY':
          return '%m-%d-%Y'
        case 'YYYY-MM-DD':
          return '&Y-%m-%d'
        case 'DD/MM/YYYY':
          return '%j/%n/%Y'
        case 'MM/DD/YYYY':
          return '%n/%j/%Y'
        case 'YYYY/MM/DD':
          return '%Y/%n/%j'
        case 'MMM D, YYYY':
          return '%M %j, %Y'
      }
      return '%j.%n.%Y'
    },
    defaultTimeFormat () {
      switch(this.pickerTimeFormat) {
        case 'H.mm':
          return '%G.%i'
        case 'H:mm':
          return '%G:%i'
        case 'h:mm A':
          return '%g:%i %A'
      }
    },
    hourlyTimeHours: {
      get () {
        return this.timeStart?.hours
      },
      set (newValue) {
        this.timeStart.hours = newValue
      }
    },
    dateStart: {
      get() {
        if (!this.range || !this.range.datestart) {
          return null
        }
        return DateTime.fromFormat(this.range.datestart, 'd.M.yyyy').toJSDate()
      },
      set(newValue) {
        if (newValue) {
          this.range.datestart = DateTime.fromJSDate(newValue).toFormat('d.M.yyyy')
        } else {
          this.range.datestart = null
        }
      }
    },
    start () {
      if (!this.dateStart) {
        return null
      }
      return DateTime.fromJSDate(this.dateStart).toFormat('yyyy-MM-dd')
    },
    shortLocale () {
      return this.locale.split('_')[0]
    },
    storageKey () {
      return 'pos-' + this.shopId + '-component-' + this.component.id
    },
    zoomParams () {
      if (this.mode === 'hourly') {
        return [
          { x_unit: 'minute', x_date: this.defaultTimeFormat, x_step: 60, x_size: 6, x_start: this.hourlyTimeHours, x_length: 3 },
          { x_unit: 'minute', x_date: this.defaultTimeFormat, x_step: 60, x_size: 12, x_start: this.hourlyTimeHours, x_length: 6 },
          { x_unit: 'minute', x_date: this.defaultTimeFormat, x_step: 60, x_size: 24, x_start: this.hourlyTimeHours, x_length: 12 },
        ]
      }
      return [
        { x_unit: 'day', x_date: '%D %j', x_step: 1, x_size: 4, x_start: 0, x_length: 1 },
        { x_unit: 'day', x_date: '%D %j', x_step: 1, x_size: 7, x_start: 0, x_length: 1 },
        { x_unit: 'day', x_date: '%D %j', x_step: 1, x_size: 15, x_start: 0, x_length: 3 },
        { x_unit: 'day', x_date: '%D %j', x_step: 1, x_size: 30, x_start: 0, x_length: 7 },
        { x_unit: 'day', x_date: '%j', x_step: 1, x_size: 45, x_start: 0, x_length: 7 },
        { x_unit: 'day', x_date: '%j', x_step: 1, x_size: 60, x_start: 0, x_length: 7 }
      ]
    },
    xUnit () {
      return this.mode === 'hourly' ? 'minute' : 'day'
    },
    xDate () {
      return this.mode === 'hourly' ? this.defaultTimeFormat : '%D %j'
    },
    xStep () {
      return this.mode === 'hourly' ? 60 : 1
    },
    xSize () {
      return this.mode === 'hourly' ? 24 : 30
    },
    xStart () {
      return this.mode === 'hourly' ? this.hourlyTimeHours : 0
    },
    xLength () {
      return this.mode === 'hourly' ? 12 : 7
    },
    scheduleLoadUrl () {
      let url = this.url
      if (!this.openSections || !this.openSections.length) {
        return url
      }
      // Resource schedule accepts numeric pool ids; product schedule accepts full keys
      const openParams = []
      const seen = Object.create(null)
      for (let i = 0; i < this.openSections.length; i++) {
        const key = String(this.openSections[i])
        const poolOnly = key.match(/^pool-(\d+)$/)
        if (poolOnly && !seen[poolOnly[1]]) {
          openParams.push(poolOnly[1])
          seen[poolOnly[1]] = true
        }
        if ((key.match(/^product-\d+$/) || key.match(/^pool-\d+/)) && !seen[key]) {
          openParams.push(key)
          seen[key] = true
        }
      }
      if (!openParams.length) {
        return url
      }
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'open=' + openParams.join(',')
    }
  },
  watch: {
    start (newValue) {
      if (newValue) {
        this.scheduler.setCurrentView(DateTime.fromFormat(newValue, 'yyyy-MM-dd').toJSDate())
      }
    },
    currentDate (newValue) {
      if (newValue) {
        this.range.datestart = DateTime.fromSeconds(Number(newValue) / 1000).toFormat('d.M.yyyy')
      }
    },
    hourlyTimeHours (newValue) {
      if (newValue) {
        this.debounceView()
      }
    }
  },
  methods: {
    timeChanged(e) {
      // To keep the scheduler and starttime on the same page regarding the startTime (and startTimeHours), we have this timeChanged -event handler that tracks the changes from the picker.
      this.fromTimeline = false
      this.pickerHours = DateTime.fromJSDate(e).hour
      this.fromPicker = true
      this.setZoomParams(this.zoomParams[this.currentZoom])
    },
    hasResources (resources) {
      if ((this.resources && this.resources.length) || (this.resourcePools && this.resourcePools.length)) { // When in ResourceReservationSchedule
        let ids = this.scheduler.matrix.timeline.y_unit.map(o => parseInt(o.key.split('-')[0])).filter(n => !!n) // Let's find out which resources are on the timeline. A bit kludgy, yuck
        let matching = ids.filter(x => resources.includes(x)) // Let's get an intersection of the affected resources and the timeline's resources
        if (matching.length) { // If there is at least one match...
          return true
        }
      }
      return false
    },
    moveBack() {
      this.scheduler._click.dhx_cal_prev_button()
    },
    moveForward() {
      this.scheduler._click.dhx_cal_next_button()
    },
    moveToToday() {
      this.dateStart = DateTime.now().toJSDate()
    },
    inputDateStart(newValue) {
      // this.input('datestart', newValue)
      this.resetHorizontalScrollPosition()
    },
    getTimelineScrollElement () {
      return this.scheduler?._els?.dhx_cal_data?.[0]
    },
    load () {
      this.scrollTop = 0
      const scrollElement = this.getTimelineScrollElement()
      if (scrollElement) {
        this.scrollTop = scrollElement.scrollTop
      }
      this.scheduler._loaded = {}
      this.scheduler.load(this.scheduleLoadUrl, 'json')
    },
    reload () {
      this.reloader(this.component.id)
    },
    memoizeDebounce: function(func, wait = 0, options = {}) {
      var mem = memoize(function() {
        return debounce(func, wait, options)
      }, options.resolver)
      return function() { mem.apply(this, arguments).apply(this, arguments) }
    },
    debounceView: debounce(function() {
      if(!this.fromTimeline) {
        // Do not manipulate timeline view if start time change was initiated by the timeline
        this.setZoomParams(this.zoomParams[this.currentZoom])
      }
    }, 0),
    /*
    clearOverview () {
      let events = this.scheduler.getEvents()
      for (let i = 0; i < events.length; i++) {
        let event = events[i]
        if (event.section_id.match(/^pool/)) {
          this.scheduler.deleteEvent(event.id)
        }
      }
    },
    */
    async setLocale () {
      switch (this.locale) {
        case 'fi_FI':
        case 'de_DE':
        case 'it_IT':
        case 'ru_RU':
        case 'sv_SE':
        case 'es_ES':
        case 'fr_FR':
          await import(`../assets/dhtmlx/locale/locale_${this.shortLocale}.js`)
          break
        case 'nb_NO':
          await import(`../assets/dhtmlx/locale/locale_no.js`)
          break
      }
    },
    async init () {
      const $this = this
      this.schedulerLoadCount = 0
      await this.setLocale()
      this.scheduler = Scheduler.getSchedulerInstance()
      guardSchedulerRender(this.scheduler)
      this.scheduler.config.container_autoresize = false
      this.scheduler.locale.labels.timeline_tab = this._('Timeline')
      this.scheduler.locale.labels.section_custom = 'Section';
      this.scheduler.config.multisection = true
      this.scheduler.config.multisection_shift_all = false
      this.scheduler.config.xml_date = '%Y-%m-%d %H:%i'
      this.scheduler.config.default_date = this.defaultDateFormat
      this.scheduler.xy.scale_height = 30
      this.scheduler.createTimelineView({
        name: 'timeline',
        x_unit: this.xUnit,   //measuring unit of the X-Axis.
        x_date: this.xDate,   //date format of the X-Axis
        x_step: this.xStep,   //X-Axis step in 'x_unit's
        x_size: this.xSize,   //X-Axis length specified as the total number of 'x_step's
        x_start: this.xStart,     //X-Axis offset in 'x_unit's
        x_length: this.xLength,    //number of 'x_step's that will be scrolled at a time
        y_unit: this.scheduler.serverList('sections'),         //sections of the view (titles of Y-Axis)
        y_property: 'section_id', //mapped data property
        dx: 400,
        event_dy: 'full',
        second_scale: {
          x_unit: "month",
          x_date: "%F %Y"
        },
        section_autoheight: false,
        resize_events: false,
        fit_events: true,
        folder_events_available: true,
        render: 'tree'             //view mode
      })
      this.scheduler.templates.timeline_scale_label = function(key, label, section) {
        let l = label
        if (section && section.hasOwnProperty('shared')) {
          if (parseInt(section.shared)) {
            l += ' <i class="fa fa-users">'
          } else {
            l += ' <i class="fa fa-users-slash">'
          }
        }
        if ($this.scheduler.matrix.timeline.dx <= 100) {
          l = '<small>' + label + '</small>'
        }
        if (key && key.match(/^product/)) {
          if ($this.scheduler.matrix.timeline.dx > 100) {
            return '<i class="fa fa-info-circle"></i> ' + label
          }
        }
        return l
      }
      this.scheduler.templates.timeline_cell_class = function(evs, date, section) {
        if(DateTime.fromJSDate(date).toFormat('d.M.yyyy') == DateTime.now().toFormat('d.M.yyyy')) {
          return 'dhx_today'
        }
        if(DateTime.fromJSDate(date).weekday === 7 || DateTime.fromJSDate(date).weekday === 6) {
          return 'dhx_weekend'
        }
        return ''
      }
      this.scheduler.templates.timeline_scalex_class = function(date) {
        if(DateTime.fromJSDate(date).weekday === 7 || DateTime.fromJSDate(date).weekday === 6) {
          return 'dhx_weekend'
        }
        return ''
      }
      this.scheduler.templates.event_class = function(start, end, ev) {
        var cls = '';
        var d = new Date();
        if(start.getTime() < d.getTime() && end.getTime() > d.getTime()) {
          cls = 'dhx_currentevent';
        } else if(end.getTime() < d.getTime()) {
          cls = 'dhx_pastevent'
        } else if(start.getTime() > d.getTime()) {
          cls = 'dhx_upcomingevent'; 
        }
        var johkuCls = 'dhx_johkuevent';
        switch (ev.order_type) {
          case 'none':
            johkuCls = 'dhx_johkureservationevent';
            break;
          case 'open':
            johkuCls = 'dhx_johkuopenorderevent';
            break;
          case 'confirmed':
            johkuCls = 'dhx_johkuconfirmedorderevent';
            break;
          case 'notpaid':
            johkuCls = 'dhx_johkuunpaidorderevent';
            break;
          case 'paid':
            johkuCls = 'dhx_johkupaidorderevent';
            break;
          case 'partial':
            johkuCls = 'dhx_johkupartialreservationevent';
            break;
          default:
            johkuCls = 'dhx_johkuevent';
            break;
        }
        if (ev.section_id && ev.section_id.match(/^product/)) {
          if (parseInt(ev.capacity) > 0) {
            johkuCls = 'dhx_johku_available'
          } else {
            johkuCls = 'dhx_johku_unavailable';
          }
        }
        if (ev.section_id && ev.section_id.match(/^pool/)) {
          johkuCls = 'dhx_johku_overview'
        }
        if (ev.class) {
          johkuCls += ' ' + ev.class;
        }
        return johkuCls + ' ' + cls;
      }
      this.scheduler.templates.event_bar_text = function (start, end, ev) {
        if (ev.section_id && ev.section_id.match(/^product/)) {
          let text = ''
          if (parseInt(ev.capacity)) {
            // text = ev.capacity + ' / ' + (parseInt(ev.units, 10) * parseInt(ev.maxcapacity, 10))
            text = ev.capacity
          }
          return '<div class="dhx_unskew dhx_center">' + text + '</div>'
        }
        if (ev.section_id && ev.section_id.match(/^pool/)) {
          return '<div class="dhx_unskew dhx_center">' + escapeHtml(ev.text) + '</div>'
        }
        return '<div class="dhx_unskew">' + escapeHtml(ev.text) + (parseInt(ev.hasnotes) ? ' <i class="fa fa-fw fa-sticky-note"></i>' : '' ) + '</div>'
      }
      this.scheduler.attachEvent('onAfterFolderToggle', function(section, isOpen, allSections) {
        // Ignore toggles caused by data load / view restore — only persist user actions
        if ($this.isSchedulerLoading() || $this.isRestoringViewState) {
          return
        }
        if (section && section.key) {
          if (isOpen) {
            if ($this.openSections.indexOf(section.key) === -1) {
              $this.openSections.push(section.key)
            }
          } else {
            var index = $this.openSections.indexOf(section.key)
            if (index !== -1) {
              $this.openSections.splice(index, 1)
            }
          }
          $this.saveViewState()
        }
      })
      // Mark folders open before the timeline rebuilds/renders from freshly loaded collections.
      // openSection() during onXLE is unsafe: it re-enters onOptionsLoad/setCurrentView and races
      // with overlapping loads (init load + restore date load), leaving sections closed.
      this.scheduler.attachEvent('onOptionsLoadStart', function () {
        if ($this.isSchedulerLoading()) {
          $this.applyPersistedOpenSections()
        }
      })
      this.scheduler.attachEvent('onXLS', function () {
        $this.beginSchedulerLoad()
        $this.loadedEvents = {}
      })
      this.scheduler.attachEvent('onEventLoading', function (event) {
        if (!schedulerEventHasDates(event)) {
          return false
        }
        $this.loadedEvents[event.id] = true
        return true
      })
      this.scheduler.attachEvent('onDataRender', function (event) {
        const scrollElement = $this.getTimelineScrollElement()
        if ($this.scrollTop && scrollElement) {
          scrollElement.scrollTop = $this.scrollTop
        }
        $this.wheelHandler()
      })
      this.scheduler.attachEvent('onXLE', function () {
        $this.clearRemovedEvents()
        $this.applyPersistedOpenSections()
        $this.endSchedulerLoad()
      })
      this.scheduler.attachEvent('onClick', function(id, e) {
        if (!id) {
          return
        }
        let event = $this.scheduler.getEvent(id)
        if (!event || !event.section_id) {
          return
        }
        // We're storing this value for the onBeforeDrag event handler to check if we're clicking on an event
        $this.isClickEvent = true
        clearTimeout($this.isClickEventTimeout)
        $this.isClickEventTimeout = setTimeout(() => {
          $this.isClickEvent = false
        }, 1000) // Reset this after one second so we can continue to drag stuff, too
        if (event.section_id.match(/^pool/)) {
          return $this.poolEventClicked(event)
        }
        if (event.section_id.match(/^product/)) {
          if (parseInt(event.capacity)) {
            return $this.hasCapacityClicked(event)
          } else {
            return $this.noCapacityClicked(event)
          }
        }
        return $this.eventClicked(event)
      })
      this.scheduler.attachEvent('onDblClick', function() {
        return false
      })
      this.scheduler.attachEvent('onYScaleClick', function (index, section, e) {
        if (e && e.target && e.target.tagName && e.target.tagName.toLowerCase() === 'i') {
          $this.showPreviewModal(section.shopid, section.source_id)
        } else {
          if (section && section.key && section.key.match && !section.key.match(/^resource/)) {
            $this.preventFolderToggle = false
            if (section.open) {
              $this.scheduler.closeSection(section.key)
            } else {
              $this.scheduler.openSection(section.key)
            }
            $this.preventFolderToggle = true
          }
        }
      })
      this.scheduler.attachEvent('onBeforeFolderToggle', function (section, isOpen, allSections) {
        if ($this.preventFolderToggle) {
          return false
        }
        return true
      })
      this.scheduler.attachEvent('onBeforeEventChanged', function(ev, e, is_new, original) {
        $this.originalEvent = original
        return true
      })
      this.scheduler.attachEvent('onBeforeEventChanged', function(ev, e, is_new, original) {
        $this.originalEvent = original
        return true
      })
      this.scheduler.attachEvent('onBeforeDrag', function(id, mode, e) {
        // Let's forcefully disable the drag event if we're actually clicking on an event
        // There's a certain delay of double clicking that's slower than the onDblClick event 
        // that causes the drag event to start.
        if ($this.isClickEvent) {
          return false
        }
        return true
      })
      this.scheduler.attachEvent('onDragEnd', async function(id, mode, e) {
        var event = null
        if (!id) {
          return
        }
        if (mode === 'new-size') { // Dragging a new event directly into a resource
          event = $this.scheduler.getEvent(id)
          if (!schedulerEventHasDates(event)) {
            return
          }
          if (($this.resources && $this.resources.length) || ($this.resourcePools && $this.resourcePools.length)) { // When in ResourceReservationSchedule
            let resArr = event.section_id.split('-')
            let resourceId = resArr[0]
            $this.hasCapacityClicked(event, resourceId)
            return
          }
          let rootSection = $this.getRootSection(event) // When in ProductReservationSchedule
          if (rootSection) {
            var evs = $this.scheduler.getEvents(event.start_date, event.end_date)
            for (let i = 0; i < evs.length; i++) {
              if (evs[i].section_id === rootSection.key) {
                event.source_id = evs[i].source_id
                event.shopid = evs[i].shopid
                let resourceId = null
                if (event.section_id && event.section_id.match(/^resource-/)) {
                  let resArr = event.section_id.split('-')
                  resourceId = resArr[1]
                }
                $this.hasCapacityClicked(event, resourceId)
                break
              }
            }
          }
          return
        }
        if (!$this.originalEvent || !$this.originalEvent.start_date) {
          return
        }
        event = $this.scheduler.getEvent(id)
        if (!schedulerEventHasDates(event)) {
          return
        }
        if (event && event.section_id && (event.section_id.match(/^pool/) || event.section_id.match(/^product/))) {
          $this.restoreHandler(event)
          return
        }
        if (id.match && id.match(/^overview/)) {
          $this.restoreHandler(event)
          return
        }
        if (event.start_date.getTime() == $this.originalEvent.start_date.getTime() && event.end_date.getTime() == $this.originalEvent.end_date.getTime() && event.section_id == $this.originalEvent.section_id) {
          $this.restoreHandler(event)
          return
        }
        let keptTime = false // This variable is no longer in use since I changed the daily mode to always preserve previous dates
        if ($this.keepTimes && event.section_id != $this.originalEvent.section_id) {
          let treshold = ($this.mode === 'daily' ? 43200000 : 1200000)
          if (Math.abs(event.start_date.getTime() - $this.originalEvent.start_date.getTime()) < treshold && Math.abs(event.end_date.getTime() - $this.originalEvent.end_date.getTime()) < treshold) {
            event.start_date = $this.originalEvent.start_date
            event.end_date = $this.originalEvent.end_date
            mode = 'move'
            keptTime = true
          }
        }
        let data = {
          id: id,
          source: 'editor'
        }
        let buttons = [
          {
            title: $this._('Save'),
            class: 'success',
            icon: 'save',
            handler: async () => {
              if ($this.mode === 'hourly') {
                data.start_date = DateTime.fromJSDate(event.start_date).toFormat('d.M.yyyy')
                data.start_time = DateTime.fromJSDate(event.start_date).toFormat('H.mm')
                data.end_date = DateTime.fromJSDate(event.end_date).toFormat('d.M.yyyy')
                data.end_time = DateTime.fromJSDate(event.end_date).toFormat('H.mm')
              } else {
                let s = DateTime.fromJSDate($this.originalEvent.start_date).set({ hour: 0, minute: 0, second: 0 })
                let e = DateTime.fromJSDate($this.originalEvent.end_date).set({ hour: 0, minute: 0, second: 0 })
                let diff = e.diff(s, 'days')
                data.start_date = DateTime.fromJSDate(event.start_date).toFormat('d.M.yyyy')
                const realStart = $this.originalEvent.real_start_date
                const realEnd = $this.originalEvent.real_end_date
                data.start_time = realStart
                  ? DateTime.fromFormat(realStart, 'yyyy-MM-dd HH:mm').toFormat('H.mm')
                  : DateTime.fromJSDate($this.originalEvent.start_date || event.start_date).toFormat('H.mm')
                if (mode == 'move') { // If we are moving, increase the end date in relation to the start date
                  data.end_date = DateTime.fromFormat(data.start_date, 'd.M.yyyy').plus({ days: diff.days }).toFormat('d.M.yyyy')
                } else if (mode == 'resize') { // If we are resizing, end date should not change
                  data.end_date = DateTime.fromJSDate(event.end_date).toFormat('d.M.yyyy')
                }
                data.end_time = realEnd
                  ? DateTime.fromFormat(realEnd, 'yyyy-MM-dd HH:mm').toFormat('H.mm')
                  : DateTime.fromJSDate($this.originalEvent.end_date || event.end_date).toFormat('H.mm')
              }
              if (event.section_id !== $this.originalEvent.section_id) {
                data.resources = $this.changeResources($this.originalEvent.section_id, event.section_id)
              } else {
                // There's really no point in doing this on the client side.
                // We could just as well add support in the back end to handle this by setting data.resources to something like "preserve"
                let resArr
                if (event.section_id.match(',')) {
                  resArr = event.section_id.split(',')
                } else {
                  resArr = [event.section_id]
                }
                resArr = resArr.map(id => {
                  const idArr = id.split('-')
                  if (idArr.length === 2) {
                    return idArr[0]
                  } else {
                    return idArr[1]
                  }
                })
                resArr = [...new Set(resArr)]
                data.resources = resArr.join(',')
              }
              $this.saveEventChanges(data, event)
            }
          },
          {
            title: $this._('Cancel'),
            icon: 'undo',
            handler: () => {
              $this.restoreHandler(event)
            }
          }
        ]
        $this.showDialog({
          title: $this._('Do you want to save the change you made to this reservation?'),
          buttons: buttons
        })
      })
      this.scheduler.showLightbox = function (id) {
        $this.scheduler.deleteEvent(id)
      }
      this.scheduler.init(this.$refs.schedulerelement, new Date(), 'timeline')
      if (this.mode === 'hourly') {
        this.scheduler.setLoadMode('day')
      } else {
        this.scheduler.setLoadMode('month')
      }
      this.load()
    },
    clearRemovedEvents() {
      let start = DateTime.fromJSDate(this.scheduler._from)
      let end = DateTime.fromJSDate(this.scheduler._to)
      let events = this.scheduler.getEvents()
      for (let i = 0; i < events.length; i++) {
        let event = events[i]
        if (!this.loadedEvents[event.id]) {
          let eventStart = DateTime.fromJSDate(event.start_date)
          let eventEnd = DateTime.fromJSDate(event.end_date)
          if (
            (eventStart.toMillis() > start.toMillis() && eventStart.toMillis() < end.toMillis()) ||
            (eventEnd.toMillis() > start.toMillis() && eventEnd.toMillis() < end.toMillis())
          ) {
            this.scheduler.deleteEvent(event.id)
          }
        }
      }
    },
    async saveEventChanges (data, event) {
      let response
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/reservations/' + data.id + '.json?apikey=' + this.apiKey, data)
      } catch (e) {
        this.showError(e)
        return this.restoreHandler(event)
      }
      this.originalEvent = null
    },
    changeResources (origStr, newStr) {
      let origSections = origStr.split(',')
      let newSections = newStr.split(',')
      let removedSections = origSections.filter(item => !newSections.includes(item))
      let addedSections = newSections.filter(item => !origSections.includes(item))
        
      let origIds = this.stripIndexes(origSections)
      let removedIds = this.stripIndexes(removedSections)
      let addedIds = this.stripIndexes(addedSections)
      
      let finalIds = []
      let id
      for (let i = 0; i < origIds.length; i++) {
        id = origIds[i]
        if(removedIds.indexOf(id) === -1) {
          finalIds.push(id)
        }
      }
      for (let i = 0; i < addedIds.length; i++) {
        id = addedIds[i]
        finalIds.push(id)
      }
      return finalIds.join(',')
    },
    stripIndexes (sArr) {
      let ret = []
      for(let i = 0; i < sArr.length; i++) {
        let iArr = sArr[i].split('-')
        if (iArr.length === 2) { // ResourceReservationSchedule has two parts (367-0)
          ret.push(iArr[0])
        } else { // ProductReservationSchedule has three parts (resource-367-0). Fuck why did I make them different?
          ret.push(iArr[1])
        }
      }
      return ret
    },
    restoreHandler (event) {
      if (!event || !this.originalEvent || !this.originalEvent.start_date) {
        this.originalEvent = null
        return
      }
      event.start_date = this.originalEvent.start_date
      event.end_date = this.originalEvent.end_date
      event.section_id = this.originalEvent.section_id
      this.originalEvent = null // Important to reset this so that the firing onDragEnd doesn't trigger a restoreHandler on the next click event
      if (event.id && this.scheduler) {
        this.scheduler.updateEvent(event.id)
        this.scheduler.setCurrentView()
      }
    },
    poolEventClicked (event) {
      // Do nothing for now
      // console.log(event)
    },
    noCapacityClicked (event) {
      // Do nothing for now
    },
    hasCapacityClicked (event, resourceId) {
      if (this.products) {
        this.productHasCapacityClicked(event, resourceId)
      } else {
        this.resourceHasCapacityClicked(event, resourceId)
      }
    },
    async productHasCapacityClicked (event, resourceId) {
      let prodArr = event.section_id.split('-')
      let productId = prodArr[1]
      let response
      try {
        response = await axios.get(this.baseDomain + event.shopid + '/products/' + event.source_id + '.json')
      } catch (e) {
        return
      }
      this.productShopId = event.shopid
      this.product = response.data
      this.summaryStart = DateTime.fromJSDate(event.start_date).toFormat('yyyy-MM-dd HH:mm')
      this.summaryEnd = DateTime.fromJSDate(event.end_date).toFormat('yyyy-MM-dd HH:mm')
      this.productStartDate = event.start_date
      this.productEndDate = event.end_date
      this.preselectedResource = resourceId || null
    },
    async resourceHasCapacityClicked (event, resourceId) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/resources/' + resourceId + '.json?apikey=' + this.apiKey)
      } catch(e) {
        return
      }
      const resource = response.data
      this.showModal('directreservation', DirectReservation, {
        props: {
          event: event,
          selectedResource: resource,
          shopId: this.shopId
        },
        events: {
          save: this.createReservation
        },
        handlers: {},
        width: 700
      })
    },
    async createReservation (data) {
      let response
      const postData = {
        status: 1,
        source: 'editor',
        resources: data.resource.id + ':::' + data.resource.name + ':::' + data.participants + ';;;',
        start_date: DateTime.fromJSDate(data.start).toFormat('d.M.yyyy'),
        start_time: DateTime.fromJSDate(data.start).toFormat('H.mm'),
        end_date: DateTime.fromJSDate(data.end).toFormat('d.M.yyyy'),
        end_time: DateTime.fromJSDate(data.end).toFormat('H.mm'),
        maintenanceMode: data.maintenanceMode
      }
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/reservations.json?apikey=' + this.apiKey, postData)
      } catch(e) {
        this.showError(e, this._('Creating the reservation failed!'))
        return
      }
      this.showNotification(this._('Reservation created!'))
      this.editReservation(response.data)
    },
    productModalClosed () {
      this.productShopId = null
      this.product = null
      this.productStartDate = null
      this.productEndDate = null
      this.preselectedResource = null
    },
    reservationAddedToBasket() {
      this.EventBus.$emit('showFloatingReceipt')
    },
    async eventClicked (event) {
      let response
      let reservation = null
      let order = null
      if (parseInt(event.id)) {
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/reservations/' + event.id + '.json?items=true&apikey=' + this.apiKey)
          reservation = response.data
        } catch(e) {}
      }
      if (parseInt(event.order_id)) {
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/orders/' + event.order_id + '.json?items=true&reservations=true&apikey=' + this.apiKey)
          order = response.data
        } catch(e) {}
      }
      this.showModal('reservationpreview', ReservationPreview, {
        props: {
          reservation: reservation,
          order: order,
          customTitle: event.text,
          pickup: event.pickup || null,
          dropoff: event.dropoff || null
        },
        events: {
          displayOrder: this.displayOrder,
          editReservation: this.editReservation,
          checkin: this.checkin(event),
          checkout: this.checkout(event),
          cancelCheckin: this.cancelCheckin(event),
          cancelCheckout: this.cancelCheckout(event),
          saveNotes: this.saveNotes(event),
          selectPreviousPartialReservation: this.selectPreviousPartialReservation(event),
          selectNextPartialReservation: this.selectNextPartialReservation(event),
        },
        handlers: {

        },
        width: 800
      })
    },
    checkin (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'in')
      }
    },
    checkout (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'out')
      }
    },
    cancelCheckin (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'in', true)
      }
    },
    cancelCheckout (event) {
      return (item) => {
        this.checkInOrOut(event, item, 'out', true)
      }
    },
    async checkInOrOut(event, item, mode, cancel) {
      let data = {
        identifier: item.identifier
      }
      if (mode === 'in') {
        if (cancel) {
          data.checkin_ts = null
        } else {
          data.checkin_ts = 'now'
        }
      } else {
        if (cancel) {
          data.checkout_ts = null
        } else {
          data.checkout_ts = 'now'
        }
      }
      let response
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/reservations/' + item.identifier + '.json?apikey=' + this.apiKey, data)
      } catch(e) {}
      this.eventClicked(event)
    },
    saveNotes () {
      return async (order) => {
        let data = {
          id: order.id,
          notes: order.notes
        }
        let response
        try {
          response = await axios.patch(this.baseDomain + this.shopId + '/orders/' + order.id + '.json?apikey=' + this.apiKey, data)
        } catch(e) {
          this.showError(e)
        }
      }
    },
    findPartialItem (order, reservation) {
      let index = null
      let count = 0
      let item = null
      for (var currItem of order.ownItem) {
        let currIndex = currItem.ownReservation.findIndex(res => res && Number(res.id) === Number(reservation.id))
        if (currIndex !== false) {
          item = currItem
          index = currIndex
          count = currItem.ownReservation.filter(res => res && Number(res.item_id) === Number(reservation.item_id)).length
          break
        }
      }
      return { item, index, count }
    },
    selectPreviousPartialReservation (event) {
      return ({ order, reservation, text }) => {
        let { item, index, count } = this.findPartialItem(order, reservation)
        if (!item) {
          return
        }
        index--
        if (index < 0) {
          index = count - 1
        }
        this.eventClicked({
          id: item.ownReservation[index].id,
          order_id: order.id,
          text: text
        })
      }
    },
    selectNextPartialReservation (event) {
      return ({ order, reservation, text }) => {
        let { item, index, count } = this.findPartialItem(order, reservation)
        if (!item) {
          return
        }
        index++
        if (index >= count) {
          index = 0
        }
        this.eventClicked({
          id: item.ownReservation[index].id,
          order_id: order.id,
          text: text
        })
      }
    },
    displayOrder (id, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ': ' + id,
        width: 6,
        data: {
          orderId: id
        },
        skipState
      })
    },
    editReservation (id, orderId, name, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ReservationDetails',
        title: this._('Reservation') + ': ' + id + (name ? ': ' + name : ''),
        data: {
          reservationId: id,
          orderId: orderId
        },
        skipState
      })
    },
    zoomIn () {
      if(this.currentZoom > 0) {
        this.setZoomParams(this.zoomParams[--this.currentZoom])
      }
    },
    zoomOut () {
      if(this.currentZoom < this.zoomParams.length - 1) {
        this.setZoomParams(this.zoomParams[++this.currentZoom]);
      }
    },
    setZoomParams (z) {
      for (var key in z) {
        if (Object.prototype.hasOwnProperty.call(z, key)) {
          this.scheduler.matrix.timeline[key] = z[key]
          if (key == 'x_date') {
            this.scheduler.templates.timeline_scale_date = (date) => {
              let func = this.scheduler.date.date_to_str(this.scheduler.matrix.timeline.x_date)
              return func(date)
            }
          } 
        }
      }
      this.scheduler.setCurrentView()
      this.saveViewState()
    },
    isSchedulerLoading () {
      return this.schedulerLoadCount > 0
    },
    beginSchedulerLoad () {
      this.schedulerLoadCount++
    },
    endSchedulerLoad () {
      if (this.schedulerLoadCount > 0) {
        this.schedulerLoadCount--
      }
    },
    saveViewState (options) {
      if (!(options && options.force) && (this.isRestoringViewState || this.isSchedulerLoading())) {
        return
      }
      this.saveViewStateToKey(this.storageKey)
    },
    saveViewStateToKey (key) {
      if (!key) {
        return
      }
      let data = {
        view: 'timeline',
        zoom: this.currentZoom,
        date: this.currentDate,
        time: this.hourlyTimeHours,
        openSections: this.openSections
      }
      localStorage.setItem(key, JSON.stringify(data))
    },
    preloadOpenSectionsFromStorage () {
      this.openSections = []
      try {
        const data = localStorage.getItem(this.storageKey)
        if (!data) {
          return
        }
        const state = JSON.parse(data)
        if (state.openSections && state.openSections.length) {
          this.openSections = state.openSections.slice()
        }
      } catch (e) {
        // Ignore invalid stored state
      }
    },
    applyPersistedOpenSections () {
      const sectionIds = this.openSections
      if (!sectionIds || !sectionIds.length || !this.scheduler) {
        return
      }
      const openSet = Object.create(null)
      for (let i = 0; i < sectionIds.length; i++) {
        openSet[sectionIds[i]] = true
      }
      const roots = (this.scheduler.matrix && this.scheduler.matrix.timeline && this.scheduler.matrix.timeline.y_unit_original)
        || this.scheduler.serverList('sections')
      if (!roots || !roots.length) {
        return
      }
      const markOpen = (nodes) => {
        let anyOpen = false
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i]
          if (node.children && node.children.length) {
            const childOpen = markOpen(node.children)
            if (openSet[node.key] || childOpen) {
              node.open = true
              anyOpen = true
            }
          } else if (openSet[node.key]) {
            anyOpen = true
          }
        }
        return anyOpen
      }
      markOpen(roots)
      // Rebuild flattened display list after marking (runs after treetimeline's own rebuild)
      if (this.scheduler.matrix && this.scheduler.matrix.timeline && this.scheduler._getArrayToDisplay) {
        this.scheduler.matrix.timeline.y_unit = this.scheduler._getArrayToDisplay(roots)
      }
    },
    restoreViewState () {
      this.isRestoringViewState = true
      try {
        let data = localStorage.getItem(this.storageKey)
        if (data) {
          let state = JSON.parse(data)
          // Ensure openSections are set before any setCurrentView-triggered loads
          if (state.openSections && state.openSections.length) {
            this.openSections = state.openSections.slice()
          }
          if (state.zoom !== false) {
            this.currentZoom = Math.min(state.zoom, this.zoomParams.length - 1)
            this.setZoomParams(this.zoomParams[this.currentZoom])
          }
          if (state.date) {
            this.scheduler.setCurrentView(new Date(state.date))
            this.range.datestart = DateTime.fromJSDate(new Date(state.date)).toFormat('d.M.yyyy')
          } else {
            this.range.datestart = DateTime.now().toFormat('d.M.yyyy')
          }
          if (state.time) {
            this.hourlyTimeHours = state.time
            if (this.mode === 'hourly') {
              this.setZoomParams(this.zoomParams[this.currentZoom])
            }
          } else {
            this.hourlyTimeHours = 8
          }
        }
      } finally {
        this.isRestoringViewState = false
      }
    },
    getRootSection (event) {
      let sections = this.scheduler.serverList('sections')
      let currParent = null
      for (let i = 0; i < sections.length; i++) {
        currParent = sections[i]
        if (currParent.children) {
          for (let j = 0; j < currParent.children.length; j++) {
            let currSection = currParent.children[j]
            if (currSection.key === event.section_id) {
              return currParent
            }
            if (currSection.children) {
              for (let k = 0; k < currSection.children.length; k++) {
                if (currSection.children[k].key === event.section_id) {
                  return currParent
                }
              }
            }
          }
        }
      }
      return false
    },
    async showPreviewModal (shopId, sourceId) {
      let response
      try {
        response = await axios.get(this.baseDomain + shopId + '/products/' + sourceId + '.json')
      } catch(e) {
        return
      }
      let product = response.data
      this.showModal('productpopup', ProductPopup, {
        props: {
          product: product,
          shopId: this.shopId,
          productShopId: shopId,
          baseDomain: this.baseDomain,
          locale: this.locale
        },
        events: {},
        handlers: {
        },
        width: 1000,
        maxWidth: 1000,
        maxHeight: 500,
        scrollable: true
      })
    },
    applyContainerHeight () {
      if (this.scheduler && this.scheduler.set_sizes) {
        this.scheduler.set_sizes()
      }
    },
    updateContainerHeight () {
      const BOTTOM_BUFFER = 10
      const MIN_HEIGHT = 200

      if (!this.$refs.schedulerelement) {
        return
      }

      if (this.$refs.schedulerelement.closest('.modal-content')) {
        this.containerHeight = this.maxHeight || '60vh'
        this.applyContainerHeight()
        return
      }

      const posColumn = this.$refs.wrapper?.closest('.pos-column')
      if (!posColumn) {
        return
      }

      const scrollAncestor = posColumn.querySelector(':scope > div:first-of-type')
      if (!scrollAncestor) {
        return
      }

      if (!this.$refs.schedulerelement) {
        return
      }
      const ancestorRect = scrollAncestor.getBoundingClientRect()
      const containerRect = this.$refs.schedulerelement.getBoundingClientRect()
      const topOffset = containerRect.top - ancestorRect.top
      // As in TimelineView.vue (JDEV-3281): end above the bottom padding that the stacked mobile columns reserve for
      // the following columns' tabs.
      const paddingBottom = parseFloat(getComputedStyle(scrollAncestor).paddingBottom) || 0
      const height = scrollAncestor.clientHeight - topOffset - Math.max(paddingBottom, BOTTOM_BUFFER)

      if (height < MIN_HEIGHT) {
        return
      }

      this.containerHeight = height
      this.applyContainerHeight()
    },
    initResizeObserver () {
      if (!this.$refs.wrapper) {
        return
      }
      const posColumn = this.$refs.wrapper.closest('.pos-column')
      if (!posColumn) {
        return
      }
      const observer = new ResizeObserver(() => {
        if (this.debouncedResizeHandler) {
          this.debouncedResizeHandler()
        }
      })
      observer.observe(posColumn)
      const nav = posColumn.querySelector('.header-navbar')
      if (nav) {
        observer.observe(nav)
      }
      this.observer = observer
    },
    resizeHandler () {
      if (!this.$refs.wrapper) {
        return
      }
      this.updateContainerHeight()
      if (!this.scheduler || !this.scheduler.matrix) {
        return
      }
      const posColumn = this.$refs.wrapper.closest('.pos-column')
      if (!posColumn) {
        return
      }
      let newDx
      let navHeight
      let columnWidth = posColumn.offsetWidth
      if (columnWidth < 600) {
        newDx = 100
        navHeight = 10
      } else if (columnWidth < 1000) {
        newDx = 240
        navHeight = 60
      } else {
        newDx = 400
        navHeight = 60
      }
      if (this.scheduler.matrix.timeline.dx != newDx) {
        this.scheduler.matrix.timeline.dx = newDx
        this.scheduler.updateView()
        this.scheduler.xy.nav_height = navHeight
        if (navHeight === 60) {
          this.isNavVisible = true
        } else {
          this.isNavVisible = false
        }
      }
      let timelineElement = document.querySelector('.dhx_cal_header')
      if (timelineElement) {
        let rect = timelineElement.getBoundingClientRect()
        this.headerTopValue = timelineElement.style.top
        if (rect.left > 0) {
          this.headerLeftValue = rect.left
        }
      }
      this.applyContainerHeight()
      if (this.resizeTimeouts && this.resizeTimeouts.length) {
        this.resizeTimeouts.forEach(id => clearTimeout(id))
      }
      this.resizeTimeouts = []
      for (let i of [10, 100, 500]) {
        this.resizeTimeouts.push(setTimeout(this.wheelHandler, i))
      }
    },
    resetHorizontalScrollPosition () {
      if (!this.$refs.wrapper) {
        return
      }
      this.$refs.wrapper.scrollLeft = 0
    },
    wheelHandler () {
      return
      // Date controls are now positioned using CSS
      /*
      let navbar = document.querySelector('.header-navbar')
      let scrollableElement = document.querySelector('.pos-column>div:first-of-type')
      let timelineElement = document.querySelector('.dhx_cal_header')
      let controlsElement = document.querySelector('.scheduler-controls')
      if (!timelineElement || !controlsElement) {
        return
      }
      let rect = timelineElement.getBoundingClientRect()
      if (scrollableElement.scrollTop > rect.top) {
        timelineElement.classList.add('position-fixed')
        timelineElement.style.top = navbar.clientHeight + "px"
        timelineElement.style.left = this.headerLeftValue + "px"
        controlsElement.classList.add('position-fixed')
        controlsElement.style.top = navbar.clientHeight + "px"
        controlsElement.style.left = this.headerLeftValue + "px"
        controlsElement.style.zIndex = "9999"
      } else {
        timelineElement.classList.remove('position-fixed')
        timelineElement.style.top = this.headerTopValue + "px"
        timelineElement.style.left = "-1px"
        controlsElement.classList.remove('position-fixed')
        controlsElement.style.top = this.headerTopValue + "px"
        controlsElement.style.left = "-1px"
      }
      */
    },
  },
  components: {
    ReservableProductListItem,
    DatetimePicker
  },
  async mounted () {
    const $this = this
    this.currentStorageKey = this.storageKey
    // Must run before init()/load() so the first schedule response can mark folders open
    this.preloadOpenSectionsFromStorage()
    this.reloader = this.memoizeDebounce(this.load, 500)
    await this.init()
    this.restoreViewState()
    this.scheduler.attachEvent('onViewChange', function (newMode, newDate) {
      $this.currentDate = newDate.getTime()
      if(newDate) {
        let newDateTime = DateTime.fromJSDate(newDate)
        if (!(newDateTime.hour == 0 && newDateTime.minute == 0)) {
          $this.fromTimeline = true
          $this.hourlyTimeHours = newDateTime.hour
        }
        if($this.fromPicker) {
          // Because the scheduler does not return the correct newDate for some mysterious reason if the viewChange is initiated by the setZoomParams function with changed x_start,
          // ie. the time from the picker, we need to hack the correct hours back if necessary.
          $this.hourlyTimeHours = $this.pickerHours
          $this.fromPicker = false
        }
      }
      $this.saveViewState('timeline')
      return true
    })
    this.debouncedResizeHandler = debounce(() => this.resizeHandler(), 100)
    window.addEventListener('resize', this.resizeHandler)
    this.resizeHandler()
    this.initResizeObserver()
    this.EventBus.$on('redrawDataTable', this.debouncedResizeHandler)
    if (!this.events && this.reloadEvents) {
      let events = {}
      for (var i = 0; i < this.reloadEvents.length; i++) {
        let eventType = this.reloadEvents[i]
        events[eventType] = (message) => {
          if (message && message.data) {
            // Only reload on availabilityCacheUpdates that affect the products in this view
            if (eventType === 'availabilityCacheUpdate' && this.products && message.data.affectedGlobalProducts) {
              let intersection = this.products.filter(x => message.data.affectedGlobalProducts.includes(parseInt(x, 10)))
              if (intersection.length) {
                return this.reload()
              }
            } else if (eventType === 'componentUpdate' && message.shopId === this.shopId) {
              if (message.data.id === this.component.id) {
                return this.reload()
              }
            } else if (eventType === 'reservationUpdate' && message.shopId === this.shopId) {
              if (message.data && message.data.id && this.loadedEvents[message.data.id]) {
                return this.reload()
              }
            } else if (message.shopId === this.shopId) { // Let's limit these to local shop, for now. If there are still problems with availability updating, remove this if and use a regular else
              return this.reload()
            }
          }
        }
      }
      events['columnRedrawTriggered'] = () => {
        this.scheduler.setCurrentView()
      }
      events['restoreDetails'] = (message) => {
        if (message.shopId !== this.shopId) {
          return
        }
        switch(message.component) {
          case 'ReservationDetails':
            this.editReservation(message.data.reservationId, message.data.orderId, null, true)
            break
          case 'OrderDetails':
            this.displayOrder(message.data.orderId, true)
            break
        }
      }
      this.events = events
    }
    this.enableEventBus()

    window.addEventListener('wheel', this.wheelHandler)
    window.addEventListener('touchmove', this.wheelHandler)
  },
  unmounted() {
    this.saveViewState({ force: true })
    window.removeEventListener('resize', this.resizeHandler)
    window.removeEventListener('wheel', this.wheelHandler)
    window.removeEventListener('touchmove', this.wheelHandler)
    if (this.resizeTimeouts && this.resizeTimeouts.length) {
      this.resizeTimeouts.forEach(id => clearTimeout(id))
      this.resizeTimeouts = []
    }
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.debouncedResizeHandler) {
      this.EventBus.$off('redrawDataTable', this.debouncedResizeHandler)
      this.debouncedResizeHandler.cancel()
    }
  },
  async updated () {
    if (this.storageKey !== this.currentStorageKey) {
      this.saveViewStateToKey(this.currentStorageKey)
      this.preloadOpenSectionsFromStorage()
      await this.init()
      this.restoreViewState()
      this.currentStorageKey = this.storageKey
    }
  }
}
</script>

<style>
.dhx_today {
  background-color: #e6ed9c !important;
}
.dhx_weekend {
  background-color: #eee;
}
@media screen and (max-width: 767px) {
  .scheduler-timeline .scheduler-wrapper {
    margin-left: -10px;
  }
  .scheduler-timeline .scheduler-wrapper {
    width: calc(100% + 35px);
  }
} 
.disable-skew .dhx_cal_event_line {
  transform: none !important;
}
.disable-skew .dhx_unskew {
  transform: none !important;
  padding: 0 !important;
}
.scheduler-timeline .dhx_scell_expand {
  margin: 0 5px 0 0 !important;
  min-width: 8px;
}
.scheduler-wrapper .scheduler {
  min-width: 0;
}
.scheduler-timeline .dhx_matrix_scell.folder .dhx_scell_name {
  text-transform: none !important;
}
.scheduler-timeline .dhx_matrix_scell.item .dhx_scell_name {
  padding-left: 0 !important;
}
.scheduler-timeline small {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.position-fixed {
  position: fixed;
}
.dhx_johkupartialreservationevent {
  color: #000;
  background-color: #ffc107;
}
.scheduler-zoom-buttons {
  display: flex;
  gap: 5px;
  z-index: 11;
  position: absolute;
  right: 10px;
  top: 10px
}
.width-xxs {
  .scheduler-controls {
    zoom: 0.87;
    left: 12px
  }
  .scheduler-zoom-buttons {
    zoom: 0.87;
  }
}

.width-sm .dhx_cal_navline .dhx_cal_date, .width-md .dhx_cal_navline .dhx_cal_date,  .width-xs.width-750px .dhx_cal_navline .dhx_cal_date,   .width-xs.width-800px .dhx_cal_navline .dhx_cal_date  {
  padding-left: 100px
}
.scheduler-top-margin {
  height: 10px;
}
.width-xxs, .width-xs.width-500px, .width-xs.width-550px, .width-xs.width-600px, .width-xs.width-650px {
  .scheduler-top-margin {
    height: 50px;
  }
}
</style>